import { log } from 'utilities/log/log';
import { FetchHeaders, Nullable } from 'types';
import userStorage from 'utilities/user/userStorage';

export function getConfig(headers?: FetchHeaders): RequestInit {
  headers = defaultHeaders(headers);
  return { method: 'GET', headers };
}

export function getRequest<T>(url: string, headers?: FetchHeaders) {
  return request<T>(url, getConfig(headers));
}

export function stringifyBody(body: Nullable<unknown>): string | null {
  return body ? JSON.stringify(body) : null;
}

export function postConfig(
  payload: Nullable<unknown>,
  headers?: FetchHeaders,
): RequestInit {
  headers = defaultHeaders(headers);
  const body = stringifyBody(payload);
  return { method: 'POST', body, headers };
}

export function postRequest<T>(url: string, body: unknown, headers?: FetchHeaders) {
  return request<T>(url, postConfig(body, headers));
}

export function deleteConfig(headers?: FetchHeaders): RequestInit {
  headers = defaultHeaders(headers);
  return { method: 'DELETE', headers };
}

export function deleteRequest<T>(url: string, headers?: FetchHeaders) {
  return request<T>(url, deleteConfig(headers));
}

export function putConfig(
  payload: Nullable<unknown>,
  headers?: FetchHeaders,
): RequestInit {
  headers = defaultHeaders(headers);
  const body = stringifyBody(payload);
  return { method: 'PUT', body, headers };
}

export async function putRequest<T>(url: string, body: unknown, headers?: FetchHeaders) {
  return request<T>(url, putConfig(body, headers));
}

export const defaultHeaders = (headers?: FetchHeaders | undefined): FetchHeaders => {
  if (!headers) {
    return { 'Content-Type': 'application/json' };
  }

  if (!headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
};

export async function request<T>(
  url: string,
  config: RequestInit,
): Promise<AppResponse<T>> {
  let response;
  try {
    response = await fetch(url, config);
  } catch (e) {
    log.debug('fetch()', e);
  }

  if (!response) {
    throw new Error('No response, please check your network connection');
  }

  let json: T = {} as T;
  try {
    json = await response.json();

    if (response.status === 401 && !url.includes('/auth/login')) {
      handleLogoutAndRedirect();
    }
  } catch (e) {
    log.debug('No JSON response from server');
  }

  return {
    json,
    raw: response,
    isOk: response.ok,
    status: response.status,
  };
}

export const handleLogoutAndRedirect = (): void => {
  userStorage.removeUserAndToken();
  window.location.href = 'login';
};

export interface AppResponse<T> {
  json: T;
  raw: Response;
  isOk: boolean;
  status: number;
}
